"use client";

import { useId, useMemo, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Paper } from "@/components/ui/paper";
import { SkeletonBox, SkeletonContainer, SkeletonLabel } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/sonner";
import { EmptyStageIllustration } from "@/components/illustrations/empty-stage-illustration";
import { useAuth } from "@/features/auth";
import { ApiError } from "@/lib/api/error";
import type { ScorecardDefinitionOut, ScorecardFillGroup, ScorecardFillOut } from "@/lib/api/model";

import { apiErrorCode } from "../hooks/use-definitions";
import { useCreateFill, useScorecardFills, useUpdateFill } from "../hooks/use-fills";
import type { ScorecardField } from "../lib/types";
import { ScorecardFillCard } from "./fill/fill-card";
import { ScorecardFillForm } from "./fill/fill-form";
import { FieldRow, ScorecardSelect } from "./shared";

function findFill(groups: ScorecardFillGroup[], id: string): ScorecardFillOut | undefined {
  return groups.flatMap((g) => g.fills).find((x) => x.id === id);
}

export function CandidateScorecards({ jobId, jcId }: { jobId: string; jcId: string }) {
  const { user } = useAuth();
  const newHintId = useId();
  const typeSelectId = useId();
  const fills = useScorecardFills(jobId, jcId);
  const create = useCreateFill(jobId, jcId);
  const update = useUpdateFill(jobId, jcId);
  const [newType, setNewType] = useState<string | "">("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ScorecardFillOut | null>(null);
  const [conflict, setConflict] = useState<{ existingFillId?: string } | null>(null);

  const data = fills.data;
  const inSet = useMemo(
    () => (data ? data.groups.filter((g) => g.in_resolved_set) : []),
    [data],
  );
  const other = useMemo(
    () => (data?.groups ?? []).filter((g) => !inSet.includes(g)),
    [data, inSet],
  );

  if (fills.isLoading) {
    return (
      <SkeletonContainer
        role="status"
        aria-label="Loading scorecards"
        className="flex-col gap-3 p-4"
      >
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col gap-2.5 rounded-lg border border-border bg-surface-primary p-3 dark:bg-surface-inset"
          >
            <div className="flex items-center gap-1.5">
              <SkeletonBox className="h-5 w-24 rounded-sm" />
              <SkeletonBox tone="muted" className="h-5 w-16 rounded-sm" />
            </div>
            <SkeletonLabel tone="muted" className="w-48" />
          </div>
        ))}
      </SkeletonContainer>
    );
  }

  if (fills.isError || !data) {
    return (
      <div className="p-4">
        <Alert.Container variant="destructive">
          <Alert.Icon>
            <Icon name="IconExclamationCircle" size={16} aria-hidden="true" />
          </Alert.Icon>
          <Alert.Content>Could not load scorecards.</Alert.Content>
        </Alert.Container>
      </div>
    );
  }

  const total = data.groups.reduce((n, g) => n + g.fills.length, 0);
  const interviewers = new Set(data.groups.flatMap((g) => g.fills.map((f) => f.interviewer.id))).size;
  const selectable = inSet.map((g) => g.definition);
  const fieldsOf = (d: ScorecardDefinitionOut) => d.current_version.fields as unknown as ScorecardField[];
  const chosen = selectable.find((d) => d.id === newType);

  const createFill = async (values: Record<string, unknown>, label: string, submit: boolean) => {
    if (!chosen) return;
    try {
      await create.mutateAsync({ scorecard_definition_id: chosen.id, external_event_id: label, values, submit });
      setCreating(false); setNewType(""); setConflict(null);
    } catch (err) {
      if (apiErrorCode(err) === "SCORECARD_FILL_EXISTS") {
        const id = err instanceof ApiError && typeof err.detail.existing_fill_id === "string"
          ? err.detail.existing_fill_id
          : undefined;
        setConflict({ existingFillId: id });
      }
    }
  };

  const openExisting = async (id: string) => {
    let found = findFill(data.groups, id);
    if (!found) {
      try {
        const result = await fills.refetch();
        found = findFill(result.data?.groups ?? [], id);
      } catch {
        /* toasted below if still missing */
      }
    }
    if (found) {
      setEditing(found);
      setCreating(false);
      setConflict(null);
    } else {
      showToast.error("Couldn't find the existing scorecard — refresh and try again.");
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="text-sm font-medium text-heading">Scorecards</h3>
          <p className="text-[13px] text-contrast/50">
            {total} scorecard{total === 1 ? "" : "s"} · {data.groups.length} type{data.groups.length === 1 ? "" : "s"} · {interviewers} interviewer{interviewers === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Button
            variant="light"
            size="sm"
            disabled={selectable.length === 0}
            aria-describedby={selectable.length === 0 ? newHintId : undefined}
            onClick={() => setCreating(true)}
            icon={<Icon name="IconPlusSmall" size={15} />}
          >
            New scorecard
          </Button>
          {selectable.length === 0 && (
            <span id={newHintId} className="max-w-[16rem] text-right text-xs text-contrast/50">
              No scorecard types available for this job.
              {data.resolved_from === "none" ? " Set up scorecard types on the Job's Scorecards tab." : ""}
            </span>
          )}
        </div>
      </div>

      {creating && (
        <Paper.Container className="flex-col items-stretch gap-3 md:flex-col md:items-stretch">
          <FieldRow label="Scorecard type" htmlFor={typeSelectId}>
            <ScorecardSelect
              id={typeSelectId}
              label="Scorecard type"
              value={newType}
              placeholder="Choose a type…"
              variant="modal"
              className="w-full sm:w-[18rem]"
              options={selectable.map((d) => ({ value: d.id, label: d.name }))}
              onChange={(id) => {
                setNewType(id);
                setConflict(null);
              }}
            />
          </FieldRow>
          {chosen && (
            <div className="border-t border-border pt-3">
              <ScorecardFillForm
                key={chosen.id}
                fields={fieldsOf(chosen)}
                showLabel
                busy={create.isPending}
                onSaveDraft={(v, l) => createFill(v, l, false)}
                onSubmit={(v, l) => createFill(v, l, true)}
                existsConflict={conflict ? { existingFillId: conflict.existingFillId, onOpenExisting: (id) => void openExisting(id) } : undefined}
              />
            </div>
          )}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setCreating(false); setNewType(""); }}
            >
              Cancel
            </Button>
          </div>
        </Paper.Container>
      )}

      {inSet.map((g) => <Group key={g.definition.id} g={g} me={user?.id} editing={editing} setEditing={setEditing} update={update} />)}
      {other.length > 0 && (
        <>
          <h4 className="mt-1 text-[13px] font-medium text-contrast/50">Other types</h4>
          {other.map((g) => <Group key={g.definition.id} g={g} me={user?.id} editing={editing} setEditing={setEditing} update={update} />)}
        </>
      )}

      {total === 0 && !creating && (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-border bg-surface-inset px-4 py-10">
          <EmptyStageIllustration draggable={false} />
          <div className="flex flex-col items-center gap-[7px] text-center">
            <h4 className="text-base font-medium text-heading">No scorecards for this candidate</h4>
            <p className="text-sm text-contrast/60">
              {selectable.length > 0
                ? "Fill one after an interview — drafts stay private until you submit."
                : "No scorecard types are available for this job yet."}
            </p>
          </div>
          {selectable.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreating(true)}
              icon={<Icon name="IconPlusSmall" size={15} />}
            >
              Fill a scorecard
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ g, me, editing, setEditing, update }: { g: ScorecardFillGroup; me?: string; editing: ScorecardFillOut | null; setEditing: (f: ScorecardFillOut | null) => void; update: ReturnType<typeof useUpdateFill> }) {
  const fields = g.definition.current_version.fields as unknown as ScorecardField[];
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <Icon name="IconChecklist" size={14} aria-hidden="true" className="shrink-0 text-muted-foreground" />
        <h4 className="text-sm font-medium text-heading">{g.definition.name}</h4>
        <Badge variant="outline-muted" size="xs" className="rounded-sm">
          {g.fills.length} {g.fills.length === 1 ? "scorecard" : "scorecards"}
        </Badge>
      </div>
      {g.fills.map((f) =>
        editing?.id === f.id ? (
          <Paper.Container key={f.id} className="flex-col items-stretch gap-3 md:flex-col md:items-stretch">
            <ScorecardFillForm fields={fields} showLabel={false} initialValues={f.values as Record<string, unknown>} busy={update.isPending}
              onSaveDraft={async (v) => {
                try {
                  await update.mutateAsync({ fillId: f.id, body: { values: v, submit: false } });
                  setEditing(null);
                } catch {
                  /* toasted by the hook */
                }
              }}
              onSubmit={async (v) => {
                try {
                  await update.mutateAsync({ fillId: f.id, body: { values: v, submit: true } });
                  setEditing(null);
                } catch {
                  /* toasted by the hook */
                }
              }} />
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Paper.Container>
        ) : (
          <ScorecardFillCard key={f.id} fill={f} fields={fields} isOwn={f.interviewer.id === me} onEdit={f.interviewer.id === me ? () => setEditing(f) : undefined} />
        ),
      )}
      {g.hidden_count > 0 && (
        <p className="text-[13px] text-contrast/50">
          {g.hidden_count} scorecard{g.hidden_count === 1 ? "" : "s"} hidden until you submit yours
        </p>
      )}
      {g.fills.length === 0 && g.hidden_count === 0 && (
        <p className="text-[13px] text-contrast/40">No fills yet</p>
      )}
    </section>
  );
}
